// "Act as client" identity state for this tab (plan v4 §2h, §6). Kept free of React and of the session
// layer so api.ts, the upload store, the SSE hub and the pending queue can all read it without cycles.
//  - sessionTag: the tag GET /me returned for the current session; sent as X-Session-Tag on every request.
//  - identityGen: bumped on this tab's own start, exit and recovery; responses from an older gen are dropped.
//  - acting: the cached Me has `impersonation` set.
//  - ended: the freeze flag (§6 Expiry UX). Writes are refused locally and one dialog explains why.
import { useSyncExternalStore } from 'react';
import type { Me, Impersonation } from '@workdesk/shared/contracts';

export type { Impersonation };
export type ActorRef = { id: string; name: string; role?: string };
/** Me plus fields @backend adds next (canImpersonate, impersonationMinutes). Optional: older servers omit them. */
export type MeX = Me & { canImpersonate?: boolean; impersonationMinutes?: number };
export const canImpersonate = (me: Me | null | undefined) => !!(me as MeX | null | undefined)?.canImpersonate;
export const impersonationMinutes = (me: Me | null | undefined) => (me as MeX | null | undefined)?.impersonationMinutes ?? 30;

export const impersonationOf = (me: Me | null | undefined): Impersonation | null => (me as MeX | null | undefined)?.impersonation ?? null;
export const sessionTagOf = (me: Me | null | undefined): string | null => (me as MeX | null | undefined)?.sessionTag ?? null;

/** sessionStorage / localStorage keys used across a start → hard navigation → exit cycle. */
export const THEME_KEY_SAVED = 'wd.impersonationTheme';
export const RETURN_KEY = 'wd.impersonationReturn';
/** localStorage marker: this browser started an impersonation that hasn't been exited. Drives boot recovery. */
export const ACTING_MARKER = 'wd.impersonating';
export const IDENTITY_CHANNEL = 'wd-identity';

type State = { tag: string | null; gen: number; acting: Impersonation | null; actingName: string | null; ended: boolean; dialog: number };
let s: State = { tag: null, gen: 0, acting: null, actingName: null, ended: false, dialog: 0 };
const listeners = new Set<() => void>();
const set = (patch: Partial<State>) => {
  s = { ...s, ...patch };
  listeners.forEach((l) => l());
};

export const getSessionTag = () => s.tag;
export const getIdentityGen = () => s.gen;
export const isImpersonating = () => !!s.acting;
export const isActingEnded = () => s.ended;
/** Name of the client being acted as (for copy like "Session as Dana ended"). */
export const actingName = () => s.actingName;

/**
 * Called by the session layer whenever the cached Me changes (during render, like setCacheOwner).
 * Idempotent. A frozen tab keeps its acting state: the cached Me is kept on purpose (§6).
 */
export function setIdentity(me: Me | null | undefined): void {
  const tag = sessionTagOf(me);
  const acting = impersonationOf(me);
  const name = acting && me ? me.name : null;
  if (tag === s.tag && acting?.expiresAt === s.acting?.expiresAt && !!acting === !!s.acting && name === s.actingName) return;
  // Silent update: render-time callers must not trigger subscriber updates in other components.
  s = { ...s, tag, acting, actingName: name, ended: acting ? s.ended : false };
  queueMicrotask(() => listeners.forEach((l) => l()));
}

/** This tab's own identity switch (start, exit, recovery): older in-flight responses are dropped. */
export function bumpIdentityGen(): number {
  set({ gen: s.gen + 1 });
  return s.gen;
}

// ---------------- freeze ----------------
/** The session as the client ended (expiry, revocation, setting off). Keeps Me and every typed text. */
export function freezeImpersonation(): void {
  if (!s.acting || s.ended) return; // deduplicated: one dialog, however many requests answer 401

  set({ ended: true, dialog: s.dialog + 1 });
}
/** Re-shows the frozen dialog (a write was refused locally). */
export function reshowFrozenDialog(): void {
  if (s.ended) set({ dialog: s.dialog + 1 });
}

// ---------------- session changed (another tab or a stale tag) ----------------
let changedHandler: (() => void) | null = null;
/** The session layer registers the reload (qc.clear + hard reload, with a toast). */
export function setSessionChangedHandler(fn: (() => void) | null) {
  changedHandler = fn;
}
let changing = false;
/**
 * A confirmed identity change for this tab (§2h): another tab started or exited, or the server says the tag
 * no longer matches. A frozen tab only shows its dialog so its typed text stays until Exit.
 */
export function onSessionChanged(): void {
  if (s.ended) {
    reshowFrozenDialog();
    return;
  }
  if (changing) return;
  changing = true;
  try {
    changedHandler?.();
  } finally {
    queueMicrotask(() => (changing = false));
  }
}

const TAB_ID = Math.random().toString(36).slice(2);
/** Tells other tabs this browser's identity changed (start, exit, recovery). This tab ignores its own notice. */
export function broadcastIdentity(): void {
  try {
    const bc = new BroadcastChannel(IDENTITY_CHANNEL);
    bc.postMessage({ type: 'identity', tab: TAB_ID });
    bc.close();
  } catch {
    /* no BroadcastChannel */
  }
}

/** Listens for identity changes made in other tabs. */
export function listenIdentity(fn: () => void): () => void {
  if (typeof BroadcastChannel === 'undefined') return () => undefined;
  const bc = new BroadcastChannel(IDENTITY_CHANNEL);
  bc.onmessage = (m: MessageEvent<{ type?: string; tab?: string }>) => {
    if (m.data?.type === 'identity' && m.data.tab !== TAB_ID) fn();
  };
  return () => bc.close();
}

// ---------------- hooks ----------------
const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};
const snap = () => s;
export function useImpersonationState() {
  return useSyncExternalStore(subscribe, snap, snap);
}
/** Disabled state + reason for the main write buttons (composer, submit, approve) once acting has ended. */
export function useActingEnded(): { ended: boolean; reason: string | undefined } {
  const st = useImpersonationState();
  return { ended: st.ended, reason: st.ended ? `Session as ${st.actingName ?? 'the client'} ended` : undefined };
}

/** First name for compact copy ("Acting as Dana"). */
export const firstName = (name: string | null | undefined) => (name ?? '').trim().split(/\s+/)[0] || (name ?? '');

/** "Alex (staff)" for an actor ref. */
export const actorTag = (a: ActorRef) => `${firstName(a.name)} (staff)`;

/** Local time like 14:32 in the device's timezone. */
export const clock = (iso: string | number | Date) => new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

export const __test = {
  state: () => s,
  reset() {
    s = { tag: null, gen: 0, acting: null, actingName: null, ended: false, dialog: 0 };
    changedHandler = null;
    changing = false;
    listeners.forEach((l) => l());
  },
};
